import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
} from '@angular/router';
import {
  WEBSOCKET_BASE_URL,
  WebSocketService,
} from '@qtek/core/websockets-core';
import { HealthcheckCoreService } from '@qtek/libs/healthcheck-core';
import { MetaCoreStore } from '@qtek/libs/meta-core';
import { AuthStore, MenuStore, UserStore } from '@qtek/libs/store';
import { WsErrorHandlerService } from '@qtek/libs/ws-error-handler';
import { isNonNullable } from '@qtek/shared/utils';
import { Observable, of } from 'rxjs';
import { filter, map, mergeMap, take, tap } from 'rxjs/operators';

@Injectable()
export class AuthGuard implements CanActivate {
  private readonly wsPingPong$ = toObservable(
    inject(MetaCoreStore).wsPingPong
  ).pipe(filter(isNonNullable));
  private authStore = inject(AuthStore);
  private menuStore = inject(MenuStore);
  private wsService = inject(WebSocketService);
  private healthCheckCoreService = inject(HealthcheckCoreService);
  private websocketBaseUrl = inject(WEBSOCKET_BASE_URL);
  private wsErrorHandler = inject(WsErrorHandlerService);
  private userStore = inject(UserStore);

  connectToWebsocket() {
    return this.authStore.getWsInitToken().pipe(
      tap(wsTkn => {
        // reconnect ws with tkn
        this.wsService.initWebsocketConnection(
          this.wsPingPong$,
          this.wsErrorHandler.errorsHandler.bind(this.wsErrorHandler),
          { wsTkn }
        );
        this.healthCheckCoreService.registerCheckHealthSubscription(
          this.wsPingPong$,
          of('/actuator/health/serverStatusService')
        );
      })
    );
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const isImpersonate =
      state.url.startsWith('/orgs/impersonate') || route.queryParamMap.has('t');

    if (isImpersonate) {
      return of(true);
    }

    /** Fetch app navigation. */
    this.menuStore.loadMain();

    return this.userStore
      .getCurrentUser({ skipRevalidateErrorHandling: true })
      .pipe(
        mergeMap(success => {
          if (!success) {
            const returnUrl = state.url !== '/' ? state.url : '';
            this.authStore.logout({
              queryParams: returnUrl ? { returnUrl } : null,
            });
            return of(false);
          }
          const initWs = this.websocketBaseUrl
            ? this.connectToWebsocket()
            : of(null);
          return initWs.pipe(map(() => true));
        }),
        mergeMap(canActivate => {
          if (!canActivate) return of(false);
          return this.wsService.init$.pipe(
            take(1),
            map(() => true)
          );
        })
      );
  }
}
